import { z } from "zod/v4";

import {
  AnalyticsChannelList,
  castToNumber,
  castToString,
  ConversionFunnelSchema,
  ConversionFunnelType,
  CountryCodesFilterList,
  CustomerTypeList,
  DeviceTypeList,
  LandingPageFilterItemSchema,
  LandingPagePayloadSchema,
  LandingPageTypeList,
  SitewideInterval,
  VisitorTypeSchema,
} from "@intelligems/ig-types";
import type { SitewideAnalyticsPageName } from "@src/api/types/dashboards";
import type { IExperienceDashboardFilters } from "@src/store/types/dashboardFilters";

export enum SitewideOrdersScope {
  allOrders = "allOrders",
  ordersMatchedToSession = "ordersMatchedToSession",
}

export enum HolidayBenchmarkingHolidayName {
  blackFriday = "black_friday",
}

export type SitewideSnapshotsFilters = {
  snapshotsEndTime: string | null;
  snapshotsStartTime: string | null;
  snapshotsTimeFrame: string | null;
  snapshotsComparisonEndTime: string | null;
  snapshotsComparisonStartTime: string | null;
  snapshotsComparisonTimeFrame: string | null;
};

export type ISitewideDashboardFilters = Pick<
  IExperienceDashboardFilters,
  | "channels"
  | "countryCodes"
  | "countryCodesFilterType"
  | "deviceType"
  | "visitorType"
  | "customerType"
  | "maxZScore"
  | "landingPage"
  | "landingPageFilters"
  | "landingPageTypes"
> &
  SitewideSnapshotsFilters & {
    startTime: string | null;
    endTime: string | null;
    isTimeRangeUpdated: boolean;
    pageName: SitewideAnalyticsPageName;
    isZScoreEnabled: boolean;
    interval: SitewideInterval;
    ordersMatchedToSession: SitewideOrdersScope;
    audienceRatioToggle: "ratio" | "absolute";
    conversionFunnel?: ConversionFunnelType;
  };

export type SiteWideOrdersMatchedToSessionType =
  | "allOrders"
  | "ordersMatchedToSession"
  | "all";

export const DEFAULT_INTERVAL: SitewideInterval = "1 WEEK";

export const ISitewideDashboardFiltersSchema = z
  .object({
    startTime: castToString.nullable(),
    endTime: castToString.nullable(),
    snapshotsEndTime: castToString.nullable(),
    snapshotsStartTime: castToString.nullable(),
    snapshotsTimeFrame: z.string().nullable(),
    snapshotsComparisonEndTime: castToString.nullable(),
    snapshotsComparisonStartTime: castToString.nullable(),
    snapshotsComparisonTimeFrame: z.string().nullable(),
    pageName: z.string(),
    // Analytics filter, so AnalyticsChannelList — not the audience-targeting
    // ChannelTypeSchema the plugin evaluates client-side.
    channels: z.enum(AnalyticsChannelList).array(),
    countryCodes: z.array(z.string()),
    countryCodesFilterType: z.enum(CountryCodesFilterList).optional(),
    deviceType: z.enum(DeviceTypeList).nullable(),
    visitorType: z.enum(VisitorTypeSchema).nullable(),
    customerType: z.enum(CustomerTypeList).nullable(),
    landingPage: LandingPagePayloadSchema.nullish(),
    landingPageFilters: LandingPageFilterItemSchema.array().optional(),
    landingPageTypes: z.enum(LandingPageTypeList).array().optional(),
    maxZScore: castToNumber.nullable(),
    isZScoreEnabled: z.boolean(),
    interval: z.string(),
    ordersMatchedToSession: z.string(),
    audienceRatioToggle: z.enum(["ratio", "absolute"]).nullable(),
    conversionFunnel: ConversionFunnelSchema.nullish(),
  })
  .partial();

export const initialSitewideDashboardFiltersState: ISitewideDashboardFilters = {
  startTime: null,
  endTime: null,
  isTimeRangeUpdated: false,
  countryCodes: [],
  countryCodesFilterType: "include",
  deviceType: "any",
  visitorType: "any",
  customerType: null,
  channels: [],
  landingPage: {
    landingPageUrlPath: null,
    landingPageUrlPathFilterType: null,
  },
  landingPageFilters: [],
  landingPageTypes: [],
  maxZScore: null,
  pageName: "home",
  isZScoreEnabled: false,
  interval: DEFAULT_INTERVAL,
  ordersMatchedToSession: SitewideOrdersScope.ordersMatchedToSession,
  snapshotsEndTime: null,
  snapshotsStartTime: null,
  snapshotsTimeFrame: null,
  snapshotsComparisonEndTime: null,
  snapshotsComparisonStartTime: null,
  snapshotsComparisonTimeFrame: null,
  audienceRatioToggle: "ratio",
};
